import React, {
  FC,
  ReactNode,
  useMemo,
  useRef,
  useEffect,
  useState,
} from "react";
import styled from "styled-components";
import { StyledComponent } from "styled-components";
import { useChapter } from "../../../contexts/chapter-context";
import Theme from "../../../styles/themes/interfaces/Theme";

export type HeadingLevel = 1 | 2 | 3 | 4;

export interface HeadingProps {
  /**
   * The heading text
   */
  text: string | null;
  /**
   * The heading level (1 = h1, 2 = h2, 3 = h3)
   */
  level?: HeadingLevel;
  /**
   * Optional class name for styling
   */
  className?: string;
  /**
   * If true, the heading will not be included in the table of contents
   */
  ignoreTOC?: boolean;
  /**
   * If true, the heading will not include numbering
   */
  ignoreNumbers?: boolean;
  /**
   * Optional value to display (for value-style headings)
   */
  value?: ReactNode;
  /**
   * If true, the heading will break before the page
   */
  breakBefore?: boolean;
  /**
   * If true, no data-break-before attribute will be added
   */
  noBreakAttr?: boolean;
  /**
   * If true, and level is 1, a prependText will be shown above the heading.
   */
  showPrepend?: boolean;
  /**
   * Text to show above a level 1 heading if showPrepend is true.
   */
  prependText?: string;
}

const baseHeadingStyles = (theme: Theme): string => `
  font-family: ${theme.titleFontFace ?? theme.fontFace};
  margin: 0;
  font-weight: ${theme.titleFontWeight};
`;

export const h2HeadingStyles = (theme: Theme): string => `
  ${baseHeadingStyles(theme)}
  color: ${theme.fg?.subTitle};
  font-size: ${theme.ptom(30)};
  margin: ${theme.spacing?.m};   
`;

export const h3TitleStyles = (theme: Theme): string => `
  ${baseHeadingStyles(theme)}
  color: ${theme.fg?.subTitle};
  font-size: ${theme.ptom(20)};
  margin: ${theme.spacing?.m};
`;

export const h4TitleStyles = (theme: Theme): string => `
  ${baseHeadingStyles(theme)}
  color: ${theme.fg?.subTitle};
  font-size: ${theme.ptom(16)};
  margin: ${theme.spacing?.m};
`;

// Style for the main title (h1)
const MainTitle = styled.h1<{ theme: Theme }>(
  ({ theme }) => `
  ${baseHeadingStyles(theme)}
  color: ${theme.fg.title};
  font-weight: 800;
  font-size: ${theme.ptom(37)};
  line-height:7.5mm};  
  margin: 0;
  margin-bottom: ${theme.spacing?.xl ?? theme.ptom(24)};
`
);

// Style for the subtitle (h2)
const h2TitleElement = styled.h2<{ theme: Theme }>(({ theme }) =>
  h2HeadingStyles(theme)
);

// Style for the sub-subtitle (h3)
const h3TitleElement = styled.h3<{ theme: Theme }>(({ theme }) =>
  h3TitleStyles(theme)
);

// Style for the sub-sub-subtitle (h4)
const h4TitleElement = styled.h4<{ theme: Theme }>(({ theme }) =>
  h4TitleStyles(theme)
);

// Style for the prepend text (h4)
const PrependTextElement = styled.h4<{ theme: Theme }>(
  ({ theme }) => `
  ${baseHeadingStyles(theme)}
  color: ${theme.fg?.subTitle};
  font-size: ${theme.ptom(14)}; 
  margin-bottom: ${theme.spacing?.s}; 
`
);

// Style for value display in headings
const StyledValue = styled.span`
  flex: 2 50%;
  text-align: right;
`;

// The heading container when it has a value
const ValueHeadingContainer = styled.div`
  display: flex;
  border-bottom: ${({ theme }) => theme.ptom(1)} dashed rgb(189, 189, 189);
  padding-bottom: ${({ theme }) => theme.ptom(12)};
  padding-top: ${({ theme }) => theme.ptom(12)};
`;

// Map heading levels to their respective components
const headingComponents = {
  1: MainTitle,
  2: h2TitleElement,
  3: h3TitleElement,
  4: h4TitleElement,
} as const;

const Heading: StyledComponent<FC<HeadingProps>, object> = styled(
  ({
    text,
    level = 1,
    className,
    ignoreTOC = false,
    ignoreNumbers = false,
    value,
    breakBefore = false,
    noBreakAttr = false,
    showPrepend = true,
    prependText = "Adviesrapport",
  }: HeadingProps) => {
    if (!text) return null;

    const chapterContext = useChapter();
    const [isFirstLevel1, setIsFirstLevel1] = useState(false);
    const headingRef = useRef<HTMLHeadingElement>(null);

    // Check if this heading is the first level 1 heading when mounted
    useEffect(() => {
      if (level === 1) {
        // Find all level 1 headings, excluding any with a class containing 'exclude'
        const allH1s = document.querySelectorAll('h1:not([class*="exclude"])');
        // Check if our heading is the first one
        if (headingRef.current && allH1s.length > 0) {
          const isFirst = headingRef.current === allH1s[0];
          setIsFirstLevel1(isFirst);
        }
      }
    }, [level, text]);

    // Memoize the title text and ID to prevent unnecessary recalculations
    const { titleText, id } = useMemo(() => {
      let generatedTitleText = text;

      if (!ignoreTOC && !ignoreNumbers) {
        generatedTitleText = chapterContext.getFormattedChapter(level, text);
      }

      return {
        titleText: generatedTitleText,
        id: text.split(" ").join("-"),
      };
    }, [text, level, ignoreTOC, ignoreNumbers, chapterContext]);

    // Get the appropriate heading component
    const HeadingElement = headingComponents[level];

    // If we have a value, render the value style heading
    if (value !== undefined) {
      return (
        <ValueHeadingContainer className={className}>
          <HeadingElement ref={headingRef} data-ignore-toc={ignoreTOC} id={id}>
            {text}
            <StyledValue>{value}</StyledValue>
          </HeadingElement>
        </ValueHeadingContainer>
      );
    }
    /* istanbul ignore next */
    // Determine the break value
    if (level === 1) {
      breakBefore = true;
    }
    const breakValue =
      level === 1
        ? isFirstLevel1
          ? "auto"
          : breakBefore
          ? "always"
          : "auto"
        : breakBefore
        ? "always"
        : "auto";
    /* istanbul ignore next */
    // Standard heading
    return (
      <div className={className}>
        {level === 1 && showPrepend && prependText && (
          <PrependTextElement
            data-ignore-toc={true}
            {...(!noBreakAttr ? { "data-break-before": breakValue } : {})}
          >
            {prependText}
          </PrependTextElement>
        )}
        <HeadingElement
          ref={headingRef}
          data-ignore-toc={ignoreTOC}
          {...(!noBreakAttr && !(level === 1 && showPrepend && prependText)
            ? { "data-break-before": breakValue }
            : {})}
          id={id}
        >
          {titleText}
        </HeadingElement>
        {!noBreakAttr && <div style={{ display: "none" }} />}
        {noBreakAttr && <div style={{ display: "none" }} />}
      </div>
    );
  }
)``;

export default Heading;
